import { useState } from 'react'

import { gql, useApolloClient } from '@apollo/client'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Paper,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import { FplDialogTitle } from 'components/common/Dialog'
import { Restricted, usePermission } from 'providers'

import { AllocatedDriverDetails } from './AllocatedDriverDetails'
import { ChargesDetails } from './ChargesDetails'
import { SelectDriverDetails } from './SelectDriverDetails'

interface IProps {
  bookingId?: string
  openDialog: boolean
  onCloseDialog: () => void
}

const AllocateDrivers = (props: IProps) => {
  const { bookingId, openDialog, onCloseDialog } = props
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [isAllowedToViewPrimaryDriversList] = usePermission('ViewPrimaryDriversList')

  const client = useApolloClient()

  const theme = useTheme()
  const maxWidthLg = useMediaQuery(theme.breakpoints.down('xl'))

  const [isAllowedToViewFeeAndChargesDetails] = usePermission('ViewBookingFeeAndChargesDetails')

  const booking = client.readFragment({
    id: `Booking:${bookingId}`,
    fragment: gql`
      fragment BookingData on Booking {
        id
        customerId
        status
        distance
        ${isAllowedToViewFeeAndChargesDetails ? 'consignmentFee' : ''}
        courierExchangeLoadId
      }
    `,
  })

  const handleFullScreen = () => {
    setIsFullScreen(!isFullScreen)
  }

  const handleCloseDialog = () => {
    onCloseDialog()
  }

  return (
    <Dialog
      open={openDialog}
      fullWidth={isAllowedToViewPrimaryDriversList}
      maxWidth={maxWidthLg ? 'lg' : false}
      fullScreen={isFullScreen}
      scroll='body'
      aria-labelledby='allocate-drivers-dialog'>
      {isAllowedToViewFeeAndChargesDetails && (
        <FplDialogTitle
          id='allocate-drivers-dialog'
          isFullScreen={isFullScreen}
          onClose={handleCloseDialog}
          onFullScreen={handleFullScreen}>
          Allocate Drivers
        </FplDialogTitle>
      )}

      <DialogContent>
        <Grid container spacing={3}>
          {isAllowedToViewPrimaryDriversList && (
            <Grid item xs={12} xl={6}>
              <Paper variant='outlined' sx={{ padding: 2 }}>
                <SelectDriverDetails
                  booking={booking}
                  onAllocateDriverSuccessfully={handleCloseDialog}
                />
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} xl={isAllowedToViewPrimaryDriversList ? 6 : 12}>
            <Stack spacing={3}>
              <Restricted to='ViewBookingFeeAndChargesDetails'>
                <Paper variant='outlined' sx={{ padding: 2 }}>
                  <ChargesDetails booking={booking} />
                </Paper>
              </Restricted>
              <AllocatedDriverDetails
                bookingId={bookingId}
                bookingStatus={booking?.status}
                courierExchangeLoadId={booking?.courierExchangeLoadId}
              />
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant='contained' color='grey' onClick={handleCloseDialog}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AllocateDrivers
